<template>
  <div style="position: relative" :style="{ height }">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { Bar } from "vue-chartjs"
  import "chartjs-adapter-moment"
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    TimeScale,
    ChartOptions,
    ChartData,
  } from "chart.js"
  import type { DataSet } from "@/components/charts/SoLineChart.vue"
  import dayjs, { Dayjs } from "dayjs"

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    TimeScale,
  )

  export default defineComponent({
    components: { Bar },
    props: {
      title: {
        type: String,
        default: null,
      },
      height: {
        type: String,
        default: "300px",
      },
      labels: {
        type: Array as PropType<Array<string | Dayjs>>,
        required: true,
      },
      dataSets: {
        type: Array as PropType<Array<DataSet>>,
        required: true,
      },
      selectedIndex: {
        type: Number as PropType<number | null>,
        default: null,
      },
    },
    emits: {
      "click:bar": (_payload: { index: number; label: string }) => true,
    },
    data() {
      return {
        currentTheme: useThemeHelper(),
      }
    },
    computed: {
      isTimeChart(): boolean {
        return this.labels.some((l) => dayjs.isDayjs(l))
      },
      chartLabels(): string[] {
        return this.labels.map((l) => (dayjs.isDayjs(l) ? l.toISOString() : l))
      },
      chartData(): ChartData<"bar"> {
        const selectedIdx = this.selectedIndex
        return {
          labels: this.chartLabels,
          datasets: this.dataSets.map((ds) => {
            const color =
              typeof ds.lineColor === "string" ? ds.lineColor : "#000000"
            return {
              label: ds.label,
              data: ds.data,
              backgroundColor: this.chartLabels.map((_, i) => {
                if (selectedIdx === null) return color
                return i === selectedIdx ? color : color + "40"
              }),
              borderWidth: 0,
            }
          }),
        }
      },
      chartOptions(): ChartOptions<"bar"> {
        return {
          responsive: true,
          maintainAspectRatio: false,
          color: this.currentTheme.colors["on-background"],
          onClick: (_, elements) => {
            if (elements[0] !== undefined) {
              this.$emit("click:bar", {
                index: elements[0].index,
                label: this.chartLabels[elements[0].index],
              })
            }
          },
          plugins: {
            legend: {
              labels: {
                color: this.currentTheme.colors["on-background"],
              },
            },
            ...(this.title && {
              title: {
                display: true,
                text: this.title,
                color: this.currentTheme.colors["on-background"],
              },
            }),
          },
          scales: {
            x: {
              ...(this.isTimeChart && { type: "time" }),
              stacked: true,
              ticks: { color: this.currentTheme.colors["on-background"] },
              grid: { color: this.currentTheme.colors["border"] },
            },
            y: {
              stacked: true,
              ticks: { color: this.currentTheme.colors["on-background"] },
              grid: { color: this.currentTheme.colors["border"] },
            },
          },
        }
      },
    },
  })
</script>
